import type { ReactNode } from "react";
import React, { useMemo, useState } from "react";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import { Box, ClickAwayListener, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import type { DateTime, LocaleOptions } from "luxon";
import { Settings } from "luxon";
import { getShortTimeZoneName } from "@cpt/shared";
import { InfoButton } from "../InfoButton/InfoButton";

export type DateTimeTextProps = {
  dateTime: DateTime;
  labelFormat: string;
};

type DisplayOption = { label: string; timeZoneName: string; dateTime: string; relative: "ahead" | "behind" | "equal" };

const TOOLTIP_FORMAT = "h:mm a, d MMM yyyy";

const colors = {
  tooltip: {
    color: "#515D70",
    header: {
      backgroundColor: "#F5F6F8",
    },
    borderColor: "#DEE1E6",
  },
};

export function DateTimeText({ dateTime, labelFormat }: DateTimeTextProps) {
  const theme = useTheme();

  const localeOptions: LocaleOptions = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- luxon types Settings defaults as plain strings
      ({
        locale: Settings.defaultLocale,
        outputCalendar: Settings.defaultOutputCalendar,
        numberingSystem: Settings.defaultNumberingSystem,
      } as LocaleOptions),
    []
  );

  const label = useMemo(() => {
    return dateTime.toFormat(labelFormat, localeOptions);
  }, [dateTime, labelFormat, localeOptions]);

  const [localDateTime, localTimeZoneName] = useMemo(() => {
    const local = dateTime.toLocal();
    return [local, getShortTimeZoneName(local, localeOptions)];
  }, [dateTime, localeOptions]);

  const displayOptions: Array<DisplayOption> = useMemo(() => {
    const utcDateTime = dateTime.toUTC();

    const isLocalAhead =
      localDateTime.day > utcDateTime.day ||
      localDateTime.month > utcDateTime.month ||
      localDateTime.year > utcDateTime.year;

    const isLocalBehind =
      localDateTime.day < utcDateTime.day ||
      localDateTime.month < utcDateTime.month ||
      localDateTime.year < utcDateTime.year;

    return [
      {
        label: "System",
        timeZoneName: "UTC",
        dateTime: utcDateTime.toFormat(TOOLTIP_FORMAT, localeOptions),
        relative: "equal",
      },
      {
        label: "Your computer",
        timeZoneName: localTimeZoneName,
        dateTime: localDateTime.toFormat(TOOLTIP_FORMAT, localeOptions),
        relative: isLocalAhead ? "ahead" : isLocalBehind ? "behind" : "equal",
      },
    ];
  }, [dateTime, localeOptions, localDateTime, localTimeZoneName]);

  const showRelativeIndicator = useMemo(() => {
    return displayOptions.some((opt) => opt.relative !== "equal");
  }, [displayOptions]);

  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const handleShow = () => {
    setShowTooltip(!showTooltip);
  };

  const handleClose = () => {
    setShowTooltip(false);
  };

  const getRelativeIndicatorIcon = (option: DisplayOption): ReactNode | undefined => {
    switch (option.relative) {
      case "ahead":
        return <NorthIcon sx={{ height: "1rem", width: "1rem" }} titleAccess={"Next day"} />;
      case "behind":
        return <SouthIcon sx={{ height: "1rem", width: "1rem" }} titleAccess={"Previous day"} />;
      case "equal":
        return undefined;
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown" touchEvent="onTouchStart">
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Typography component="span">{label}</Typography>

        <Tooltip
          arrow
          placement={"bottom-end"}
          onClose={handleClose}
          open={showTooltip}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          title={
            <>
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                  backgroundColor: colors.tooltip.header.backgroundColor,
                  color: colors.tooltip.color,
                  borderBottomWidth: 1,
                  borderBottomStyle: "solid",
                  borderBottomColor: colors.tooltip.borderColor,
                  borderTopLeftRadius: "4px",
                  borderTopRightRadius: "4px",
                }}
              >
                Time conversion
              </Box>
              <Stack spacing={0} direction={"column"}>
                {displayOptions.map((opt, idx) => {
                  return (
                    <Stack
                      spacing={0}
                      direction={"row"}
                      key={opt.label}
                      sx={{
                        py: 0.25,
                        px: 1,

                        //show the border at the top except on the first item
                        borderTopWidth: idx !== 0 ? 1 : 0,

                        borderTopStyle: "solid",
                        borderTopColor: colors.tooltip.borderColor,
                      }}
                    >
                      <Box
                        sx={{
                          flexGrow: 1,
                          mr: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            color: colors.tooltip.color,
                            fontSize: "0.75rem",
                          }}
                        >
                          <strong>{opt.label}</strong> ({opt.timeZoneName})
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          maxWidth: "9rem",
                          ml: 2,
                          textAlign: "right",
                        }}
                      >
                        <Typography
                          sx={{
                            color: colors.tooltip.color,
                            fontSize: "0.75rem",
                          }}
                        >
                          {opt.dateTime}
                        </Typography>
                      </Box>
                      {showRelativeIndicator && (
                        <Box
                          sx={{
                            width: "1rem",
                            height: "1rem",
                            color: colors.tooltip.color,
                          }}
                        >
                          {opt.relative !== "equal" && getRelativeIndicatorIcon(opt)}
                        </Box>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </>
          }
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: theme.palette.common.white,
                maxWidth: "350px",
                boxShadow: 3,
                padding: 0,
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: colors.tooltip.borderColor,
              },
            },
            arrow: {
              sx: {
                color: colors.tooltip.header.backgroundColor,
                "&::before": {
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: colors.tooltip.borderColor,
                },
              },
            },
          }}
        >
          <InfoButton
            aria-label="Time zone information"
            sx={{
              ml: 1,
              w: 2.5,
              h: 2.5,
              p: 0,
            }}
            onClick={handleShow}
          />
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
}
